import React, { useMemo } from 'react';

// Components
import TaxAnalysisItemSelector from './components/TaxAnalysisItemSelector';

// Hooks
import { useComparisonItemsSelector } from './hooks/useComparisonItemsSelector';

// Types
import ComparisonItem from '@/types/comparison-item';

// Utils
import { PORTFOLIO_TYPES } from '@/utils/portfolios';

interface TaxAnalysisComparisonItemsSelectorProps {
  availableComparisonItems: ComparisonItem[];
  onEditItem?: (item: ComparisonItem) => void;
  readOnly?: boolean;
}

const TaxAnalysisComparisonItemsSelector: React.FC<TaxAnalysisComparisonItemsSelectorProps> = ({ availableComparisonItems, onEditItem, readOnly = false }) => {
  const { originalItem, targetItem, handleRemoveItem, handleSelectItem, handleEditItem } = useComparisonItemsSelector(onEditItem);

  const filteredBaseComparisonItems = useMemo(
    () => availableComparisonItems.filter(item => item.portfolioType !== PORTFOLIO_TYPES.TEMPLATE && item.portfolioType !== PORTFOLIO_TYPES.FIRM),
    [availableComparisonItems],
  );

  const selectors = useMemo(
    () => [
      {
        id: originalItem?._id,
        addLabel: 'Comparison.TaxAnalysis.SelectPortfolio',
        availableItems: filteredBaseComparisonItems,
        isOriginal: true,
        selectedItem: originalItem,
        otherSelectedItem: targetItem,
      },
      {
        id: targetItem?._id,
        addLabel: 'Comparison.TaxAnalysis.SelectTargetPortfolio',
        availableItems: availableComparisonItems,
        isOriginal: false,
        selectedItem: targetItem,
        otherSelectedItem: originalItem,
        disabled: !originalItem,
      },
    ],
    [originalItem, targetItem, filteredBaseComparisonItems],
  );

  return (
    <div className="comparison-item-selections">
      {selectors.map((selector, index) => (
        <TaxAnalysisItemSelector
          key={selector?.id ?? `selector-${index}`}
          addLabel={`Comparison.TaxAnalysis.${index === 0 ? 'SelectPortfolio' : 'SelectTargetPortfolio'}`}
          availableComparisonItems={selector.availableItems}
          onRemoveItem={handleRemoveItem}
          onSelectItem={handleSelectItem}
          onEditItem={handleEditItem}
          isOriginal={selector.isOriginal}
          selectedItem={selector.selectedItem}
          otherSelectedItem={selector.otherSelectedItem}
          readOnly={readOnly || selector.disabled}
        />
      ))}
    </div>
  );
};

export default TaxAnalysisComparisonItemsSelector;
